import { Dropdown } from "antd";
import { CheckIcon, ChevronDownIcon, type LucideIcon } from "lucide-react";
import { NavLink } from "react-router";
import { getPortalInitials } from "../lib/branding";
import { PortalBrand } from "./PortalProduct";

export type PortalNavItem = {
  label: string;
  path: string;
  icon: LucideIcon;
  exact: boolean;
};

export type PortalSidebarTenant = {
  slug: string;
  name: string;
  current: boolean;
};

export type PortalSidebarProps = {
  navItems: PortalNavItem[];
  isItemActive: (path: string, exact: boolean) => boolean;
  organisationName: string;
  organisationImage?: string | null;
  tenants: PortalSidebarTenant[];
  switchingTenantSlug: string | null;
  onSwitchTenant: (slug: string) => void;
  onNavigate?: () => void;
};

/** Shared dropdown styling for portal menus. */
export const portalDropdownClassName =
  "[&_.ant-dropdown-menu]:!rounded-xl [&_.ant-dropdown-menu]:!border [&_.ant-dropdown-menu]:!border-gray-200 [&_.ant-dropdown-menu]:!p-1.5 [&_.ant-dropdown-menu]:!shadow-[0_12px_32px_rgba(19,27,42,0.10)] [&_.ant-dropdown-menu-item]:!rounded-lg";

const navLinkClassName = (isActive: boolean) =>
  [
    "flex min-h-10 items-center gap-3 rounded-lg border px-3 text-sm font-medium no-underline transition-colors",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--brand-primary)/50",
    isActive
      ? "!border-transparent !bg-(--brand-secondary-soft) !text-white"
      : "!border-transparent !text-white/70 hover:!bg-white/10 hover:!text-white",
  ].join(" ");

/**
 * Renders the agency portal sidebar: workspace switcher and primary navigation.
 * Used directly on desktop and inside a drawer on small screens.
 */
export const PortalSidebar = ({
  navItems,
  isItemActive,
  organisationName,
  organisationImage,
  tenants,
  switchingTenantSlug,
  onSwitchTenant,
  onNavigate,
}: PortalSidebarProps) => {
  const brand = organisationImage ? (
    <span className="flex max-w-full items-center rounded-lg bg-white px-2 py-0.5">
      <PortalBrand name={organisationName} image={organisationImage} compact navbar />
    </span>
  ) : (
    <span className="flex min-w-0 items-center gap-2.5">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-(--portal-accent) text-sm font-bold text-(--portal-accent-ink)">
        {getPortalInitials(organisationName).slice(0, 1)}
      </span>
      <span className="min-w-0 text-left">
        <span className="portal-title block truncate text-[15px] font-semibold leading-tight text-white">
          {organisationName}
        </span>
        <span className="block text-[10px] font-medium uppercase tracking-[0.12em] text-white/50">Partner portal</span>
      </span>
    </span>
  );

  return (
    <div className="flex h-full flex-col bg-(--brand-secondary) text-white">
      <div className="flex h-16 shrink-0 items-center border-b border-white/10 px-4">
        {tenants.length > 1 ? (
          <Dropdown
            trigger={["click"]}
            placement="bottomLeft"
            rootClassName={portalDropdownClassName}
            menu={{
              items: tenants.map((tenant) => ({
                key: tenant.slug,
                disabled: tenant.current || Boolean(switchingTenantSlug),
                icon: tenant.current ? <CheckIcon size={15} className="text-(--brand-primary)" /> : undefined,
                label: (
                  <div className="min-w-48 py-0.5">
                    <p className="text-sm font-semibold text-[#131B2A]">{tenant.name}</p>
                    <p className="mt-0.5 text-xs text-gray-500">
                      {tenant.current
                        ? "Current workspace"
                        : switchingTenantSlug === tenant.slug
                          ? "Switching workspace"
                          : "Open workspace"}
                    </p>
                  </div>
                ),
                onClick: () => onSwitchTenant(tenant.slug),
              })),
            }}
          >
            <button
              type="button"
              aria-label={`Switch workspace. Current workspace: ${organisationName}`}
              className="flex w-full min-w-0 items-center gap-2 rounded-xl px-1.5 py-1 text-left text-slate-300 transition-colors hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
            >
              <span className="min-w-0 flex-1">{brand}</span>
              <ChevronDownIcon className="h-3.5 w-3.5 shrink-0 text-slate-400" strokeWidth={1.8} />
            </button>
          </Dropdown>
        ) : (
          <div className="min-w-0 px-1.5">{brand}</div>
        )}
      </div>

      <nav aria-label="Partner portal navigation" className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
        {navItems.map(({ label, path, icon: Icon, exact }) => {
          const isActive = isItemActive(path, exact);
          return (
            <NavLink
              key={path}
              to={path}
              onClick={onNavigate}
              className={() => navLinkClassName(isActive)}
              aria-current={isActive ? "page" : undefined}
            >
              <Icon size={17} strokeWidth={1.8} className={isActive ? "text-(--portal-accent)" : "text-white/50"} />
              <span className="flex-1 truncate">{label}</span>
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
};
